import { z } from 'zod'

import { isoDateTime } from '../lib/date'

export const INVOICE_SOURCES = {
  dispensing: 'dispensing',
  noShowFee: 'no_show_fee',
  procedureRequest: 'procedure_request',
  subscriptionUpgrade: 'subscription_upgrade',
} as const

export const invoiceSourceSchema = z.enum(Object.values(INVOICE_SOURCES))
export type InvoiceSource = z.infer<typeof invoiceSourceSchema>

export const invoiceStatusSchema = z.enum(['draft', 'open', 'paid', 'uncollectible', 'void'])
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>

export const invoiceLineItemSchema = z.object({
  /** `item` is a line Stripe billed; `discount` and `tax` rows are added so the rows sum to the total. */
  kind: z.enum(['item', 'discount', 'tax']),
  description: z.string().nullable(),
  amount: z.number().int(),
  quantity: z.number(),
})

export type InvoiceLineItem = z.output<typeof invoiceLineItemSchema>

export const invoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: invoiceStatusSchema,
  source: invoiceSourceSchema.nullable(),
  amountDue: z.number().int(),
  amountPaid: z.number().int(),
  currency: z.string(),
  description: z.string().nullable(),
  /** When the invoice was created. */
  createdAt: isoDateTime,
  /** Invoice issue date, honouring Stripe backdating. */
  issuedAt: isoDateTime,
  /** When payment is due, if set. */
  dueDate: isoDateTime.nullable(),
  /** When the invoice was paid. */
  paidAt: isoDateTime.nullable(),
  hostedInvoiceUrl: z.string().nullable(),
  dashboardInvoiceUrl: z.string(),
  /** Server-derived: open, has an outstanding balance, and Stripe has retried collection at least once. */
  overdue: z.boolean(),
  /** True when this open invoice is holding a subscription plan change pending payment. */
  pendingSubscriptionUpdate: z.boolean(),
  /** Stripe PaymentIntent client secret for open invoices, enabling client-side payment confirmation via Stripe.js. */
  clientSecret: z.string().nullable(),
})

export type Invoice = z.output<typeof invoiceSchema>
